import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

const CUSTOM_DOMAIN_HOSTS = new Set(["aristaconcreto.com", "www.aristaconcreto.com"]);
const RUNTIME_BACKEND_STORAGE_KEY = "arista-runtime-backend-base-v1";

const normalizeBaseUrl = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  return value.trim().replace(/\/+$/, "");
};

const getStableBackendCandidates = () => {
  const candidates = [];

  if (typeof window !== "undefined" && CUSTOM_DOMAIN_HOSTS.has(window.location.hostname)) {
    candidates.push(window.location.origin);
  }

  if (process.env.REACT_APP_BACKEND_URL) {
    candidates.push(process.env.REACT_APP_BACKEND_URL);
  }

  return [...new Set(candidates.map(normalizeBaseUrl).filter(Boolean))];
};

export function getBackendBaseCandidates() {
  return getStableBackendCandidates();
}

export function persistBackendBaseUrl(nextBaseUrl) {
  if (typeof window === "undefined") {
    return;
  }

  const normalizedNextBaseUrl = normalizeBaseUrl(nextBaseUrl);
  const stableCandidates = getStableBackendCandidates();
  if (!normalizedNextBaseUrl || !stableCandidates.includes(normalizedNextBaseUrl)) {
    return;
  }

  window.__ARISTA_BACKEND_BASE_URL__ = normalizedNextBaseUrl;

  try {
    window.localStorage.setItem(RUNTIME_BACKEND_STORAGE_KEY, normalizedNextBaseUrl);
  } catch {
    // noop
  }
}

export function getBackendBaseUrl() {
  const stableCandidates = getStableBackendCandidates();

  if (typeof window !== "undefined") {
    const runtimeCandidate = normalizeBaseUrl(window.__ARISTA_BACKEND_BASE_URL__);
    if (runtimeCandidate && stableCandidates.includes(runtimeCandidate)) {
      return runtimeCandidate;
    }

    try {
      const persisted = normalizeBaseUrl(window.localStorage.getItem(RUNTIME_BACKEND_STORAGE_KEY));
      if (persisted && stableCandidates.includes(persisted)) {
        window.__ARISTA_BACKEND_BASE_URL__ = persisted;
        return persisted;
      }
    } catch {
      // noop
    }
  }

  return stableCandidates[0] || normalizeBaseUrl(process.env.REACT_APP_BACKEND_URL);
}
const INTERNAL_MEDIA_PREFIXES = ["/api/", "/generated-designs/", "/allies/"];
const EXTERNAL_IMAGE_PROXY_HOSTS = new Set([
  "customer-assets.emergentagent.com",
  "www.aristaconcreto.com",
  "aristaconcreto.com",
  "luxury-concrete-1.preview.emergentagent.com",
  "static.wixstatic.com",
  "localhost",
  "127.0.0.1",
]);
const LOCAL_PROXYABLE_PREFIXES = ["/generated-designs/"];
export const CELOSIAS_EDITORIAL_COVER_URL = "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/4jywsfn9_ChatGPT%20Image%2023%20abr%202026%2C%2011_50_14%20p.m..png";
const COLLECTION_IMAGE_FALLBACKS = {
  celosias: CELOSIAS_EDITORIAL_COVER_URL,
};
const POTENTIALLY_UNSTABLE_IMAGE_HOSTS = new Set(["www.aristaconcreto.com"]);
const LEGACY_MEDIA_URL_REPLACEMENTS = {
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Caribe-Pieza.png": "/api/assets/69e8ba479b17b3bfb8d7b1e7",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Coba-Pieza.jpg": "/api/assets/69e8ba489b17b3bfb8d7b1ea",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Cubo-Circulo-Pieza.jpg": "/api/assets/69e8ba489b17b3bfb8d7b1ec",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Cubo-Cuadro-Pieza.jpg": "/api/assets/69e8ba489b17b3bfb8d7b1ef",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Cubo-Triangulo-Pieza.jpg": "/api/assets/69e8ba489b17b3bfb8d7b1f1",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Esfera-Pieza.jpg": "/api/assets/69e8ba489b17b3bfb8d7b1f4",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Espuma-Pieza.jpg": "/api/assets/69e8ba499b17b3bfb8d7b1f6",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Loft-Pieza.jpg": "/api/assets/69e8ba499b17b3bfb8d7b1f8",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Malla-Pieza.jpg": "/api/assets/69e8ba499b17b3bfb8d7b1fa",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Punto-de-Cruz-Pieza.jpg": "/api/assets/69e8ba499b17b3bfb8d7b1fc",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Tulum-Pieza.jpg": "/api/assets/69e8ba499b17b3bfb8d7b1fe",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Uxmal-I-Pieza.jpg": "/api/assets/69e8ba499b17b3bfb8d7b200",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Valla-Pieza.jpg": "/api/assets/69e8ba4a9b17b3bfb8d7b202",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Vallarta-Pieza.jpg": "/api/assets/69e8ba4a9b17b3bfb8d7b204",
  "https://www.aristaconcreto.com/wp-content/uploads/2024/06/Copia-de-Variante-Pieza.png": "/api/assets/69e8ba4a9b17b3bfb8d7b206",
  "https://67.225.154.231/plesk-site-preview/www.aristaconcreto.com/https/67.225.154.231/wp-content/uploads/elementor/thumbs/Arrecife-Pieza-qwoopycspqf78q5s7g39udjlybw74hg0kh1bravhn4.png": "/api/assets/69e8ba459b17b3bfb8d7b1cc",
  "https://67.225.154.231/plesk-site-preview/www.aristaconcreto.com/https/67.225.154.231/wp-content/uploads/elementor/thumbs/Boomer-2-scaled-qwo8jimzwf3z766k86ohbuoas4neka7mtoh3fwgj68.jpeg": "/api/assets/69e8ba459b17b3bfb8d7b1ce",
  "https://67.225.154.231/plesk-site-preview/www.aristaconcreto.com/https/67.225.154.231/wp-content/uploads/elementor/thumbs/Cenote-Pieza-qwq9n93ivhr9fbfuqrmngqqu3h5vsc0mnk3xbucxzk.png": "/api/assets/69e8ba459b17b3bfb8d7b1d4",
  "https://67.225.154.231/plesk-site-preview/www.aristaconcreto.com/https/67.225.154.231/wp-content/uploads/elementor/thumbs/Copia-de-Arista-Pieza-qpy0uuo87nt8cy17cxrdd3z5pei9y9kgq1exs9yt5c.png": "/api/assets/69e8ba459b17b3bfb8d7b1ca",
  "https://67.225.154.231/plesk-site-preview/www.aristaconcreto.com/https/67.225.154.231/wp-content/uploads/elementor/thumbs/Copia-de-Creciente-Pieza-scaled-qu2l78tgab3er8dbp2y5tpo0c1f29sky8i4kzmenm8.jpg": "/api/assets/69e8ba459b17b3bfb8d7b1d6",
  "https://67.225.154.231/plesk-site-preview/www.aristaconcreto.com/https/67.225.154.231/wp-content/uploads/elementor/thumbs/Copia-de-IMG_0160-scaled-qpy173ij8ykpm28uuqbeciqcb45f8b6kqndnv1t634.jpg": "/api/assets/69e8ba459b17b3bfb8d7b1d0",
  "https://67.225.154.231/plesk-site-preview/www.aristaconcreto.com/https/67.225.154.231/wp-content/uploads/elementor/thumbs/Copia-de-IMG_0306-scaled-qpy2ht3svkiokge93f6yr8dz1fx2qtmdhuhbf2juvk.jpg": "/api/assets/69e8ba459b17b3bfb8d7b1d2",
  "https://67.225.154.231/plesk-site-preview/www.aristaconcreto.com/https/67.225.154.231/wp-content/uploads/elementor/thumbs/Copia-de-T007-scaled-qtyn1f05a1697uau7vesw6g38mjihd5pxltc56ntw0.jpeg": "/api/assets/69e8ba449b17b3bfb8d7b1c8",
  "https://67.225.154.231/plesk-site-preview/www.aristaconcreto.com/https/67.225.154.231/wp-content/uploads/elementor/thumbs/DALIA-SUPERIOR-qwpz4olkclivh3c0bmmvqrul5t30ou4fltaw4xo1zk.png": "/api/assets/69e8ba469b17b3bfb8d7b1d8",
  "https://67.225.154.231/plesk-site-preview/www.aristaconcreto.com/https/67.225.154.231/wp-content/uploads/elementor/thumbs/Desfase-A-scaled-qu9879bouzcadwk5apprz0pwl7btd6x66f3dq35dzk.jpg": "/api/assets/69e8ba469b17b3bfb8d7b1da",
  "https://67.225.154.231/plesk-site-preview/www.aristaconcreto.com/https/67.225.154.231/wp-content/uploads/elementor/thumbs/Desfase-B-scaled-qu98olgiwz2oizdk27i1yn7z11vjc7qxw89sdtfz80.jpeg": "/api/assets/69e8ba469b17b3bfb8d7b1dc",
};

const LEGACY_UNSAFE_URL_PATTERNS = ["plesk-site-preview", "67.225.154.231", "wp-content/uploads"];

function getLegacyMediaCandidates(src) {
  if (!src) {
    return [];
  }

  const candidates = new Set([src]);

  try {
    const decoded = decodeURIComponent(src);
    candidates.add(decoded);

    if (decoded.includes("/wp-content/uploads/")) {
      const suffix = decoded.split("/wp-content/uploads/")[1];
      if (suffix) {
        candidates.add(`https://www.aristaconcreto.com/wp-content/uploads/${suffix}`);
      }
    }
  } catch {
    // noop
  }

  return [...candidates];
}

function resolveLegacyMediaReplacement(src) {
  const candidates = getLegacyMediaCandidates(src);
  return candidates.map((candidate) => LEGACY_MEDIA_URL_REPLACEMENTS[candidate]).find(Boolean) || null;
}

function looksLikeBrokenLegacyMediaUrl(src) {
  return LEGACY_UNSAFE_URL_PATTERNS.some((pattern) => src?.includes(pattern));
}

function normalizeApiAssetOrigin(src) {
  const legacyReplacement = resolveLegacyMediaReplacement(src);

  if (legacyReplacement) {
    return legacyReplacement;
  }

  if (!src) {
    return src;
  }

  if (looksLikeBrokenLegacyMediaUrl(src)) {
    return null;
  }

  if (src.startsWith("/api/")) {
    return src;
  }

  if (!getBackendBaseUrl()) {
    return src;
  }

  try {
    const assetUrl = new URL(src);

    if (!assetUrl.pathname.startsWith("/api/")) {
      return src;
    }

    return `${assetUrl.pathname}${assetUrl.search}`;
  } catch {
    return src;
  }
}

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function buildBackendAssetUrl(src) {
  return normalizeApiAssetOrigin(src);
}

export function inferPreferredImageFallbackFormat(src) {
  const normalizedSrc = buildBackendAssetUrl(src) || src || "";
  const lowercaseSrc = normalizedSrc.toLowerCase();
  if (lowercaseSrc.startsWith("data:image/png") || lowercaseSrc.includes(".png")) {
    return "png";
  }
  return "jpeg";
}

export function isInternalMediaPath(src) {
  return INTERNAL_MEDIA_PREFIXES.some((prefix) => src?.startsWith(prefix));
}

export function resolveCollectionImageSrc(collectionId, src) {
  const normalizedSrc = buildBackendAssetUrl(src);
  const fallbackSrc = COLLECTION_IMAGE_FALLBACKS[collectionId];

  if (!fallbackSrc) {
    return normalizedSrc;
  }

  return normalizedSrc || fallbackSrc;
}

export function isPotentiallyUnstableImageUrl(src) {
  if (!src) {
    return false;
  }

  if (looksLikeBrokenLegacyMediaUrl(src)) {
    return true;
  }

  try {
    const parsed = new URL(src);
    return POTENTIALLY_UNSTABLE_IMAGE_HOSTS.has(parsed.hostname) || parsed.hostname === "67.225.154.231";
  } catch {
    return false;
  }
}

export function pickStableImage(...sources) {
  return sources.find((src) => src && !isPotentiallyUnstableImageUrl(src)) || sources.find(Boolean) || "";
}

export function normalizeDesignMedia(design) {
  if (!design) {
    return design;
  }

  const normalized = {
    ...design,
    thumbnail: buildBackendAssetUrl(design.thumbnail),
    detailImage: buildBackendAssetUrl(design.detailImage),
    contextImage: buildBackendAssetUrl(design.contextImage),
    measurementImage: buildBackendAssetUrl(design.measurementImage),
    photoReelImages: Array.isArray(design.photoReelImages)
      ? design.photoReelImages.map((image) => ({
          ...image,
          src: buildBackendAssetUrl(image?.src),
        }))
      : [],
  };

  const stablePrimary = pickStableImage(
    normalized.thumbnail,
    normalized.detailImage,
    normalized.contextImage,
    normalized.photoReelImages?.find((image) => image?.src)?.src,
  ) || buildBrandPlaceholderDataUrl(design.title?.slice(0, 2)?.toUpperCase() || "A+V");

  const thumbnailSet = buildResponsiveImageSet(normalized.thumbnail || stablePrimary, {
    thumbnailWidth: 360,
    mediumWidth: 560,
    largeWidth: 920,
    quality: 60,
  });
  const detailSet = buildResponsiveImageSet(normalized.detailImage || stablePrimary, {
    thumbnailWidth: 560,
    mediumWidth: 920,
    largeWidth: 1440,
    quality: 68,
  });
  const contextSet = buildResponsiveImageSet(normalized.contextImage || stablePrimary, {
    thumbnailWidth: 560,
    mediumWidth: 920,
    largeWidth: 1440,
    quality: 66,
  });
  const measurementSet = buildResponsiveImageSet(normalized.measurementImage || stablePrimary, {
    thumbnailWidth: 420,
    mediumWidth: 820,
    largeWidth: 1280,
    quality: 64,
  });

  return {
    ...normalized,
    primaryImage: stablePrimary,
    thumbnailUrl: thumbnailSet.thumbnailUrl,
    mediumUrl: detailSet.mediumUrl,
    largeUrl: detailSet.largeUrl,
    originalUrl: buildBackendAssetUrl(stablePrimary),
    responsiveMedia: {
      thumbnail: thumbnailSet,
      detail: detailSet,
      context: contextSet,
      measurement: measurementSet,
    },
    thumbnail: normalized.thumbnail || stablePrimary,
    detailImage: normalized.detailImage || stablePrimary,
    contextImage: normalized.contextImage || stablePrimary,
  };
}

export function buildBrandPlaceholderDataUrl(label = "A + V") {
  const safeLabel = encodeURIComponent(label);

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 600" role="img" aria-label="${safeLabel}">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#F8F5EF" />
          <stop offset="100%" stop-color="#E7E0D5" />
        </linearGradient>
      </defs>
      <rect width="600" height="600" rx="56" fill="url(#bg)" />
      <rect x="74" y="74" width="452" height="452" rx="42" fill="none" stroke="#D4C8B8" stroke-width="6" />
      <text x="300" y="320" text-anchor="middle" font-family="Georgia, Times New Roman, serif" font-size="92" letter-spacing="12" fill="#6F6255">${label}</text>
      <text x="300" y="392" text-anchor="middle" font-family="Arial, sans-serif" font-size="24" letter-spacing="8" fill="#8D7D6A">ARISTA + VÉRTICE</text>
    </svg>
  `)}`;
}

function buildExternalProxyImageUrl(src, { width, quality = 72, format = "webp" } = {}) {
  const backendBaseUrl = getBackendBaseUrl();

  if (!backendBaseUrl || !src) {
    return src;
  }

  try {
    const parsed = new URL(src);

    if (!EXTERNAL_IMAGE_PROXY_HOSTS.has(parsed.hostname)) {
      return src;
    }

    const params = new URLSearchParams({
      url: src,
      quality: String(quality),
      format,
    });

    if (width) {
      params.set("width", String(width));
    }

    return `${backendBaseUrl}/api/image-proxy?${params.toString()}`;
  } catch {
    return src;
  }
}

function resolveSameOriginPublicAssetUrl(src) {
  if (!src || !LOCAL_PROXYABLE_PREFIXES.some((prefix) => src.startsWith(prefix))) {
    return null;
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return new URL(src, window.location.origin).toString();
  }

  const backendBaseUrl = getBackendBaseUrl();
  if (!backendBaseUrl) {
    return null;
  }

  try {
    return new URL(src, backendBaseUrl).toString();
  } catch {
    return null;
  }
}

function buildSameOriginProxyImageUrl(src, { width, quality = 72, format = "webp" } = {}) {
  const backendBaseUrl = getBackendBaseUrl();

  if (!backendBaseUrl || !src || !LOCAL_PROXYABLE_PREFIXES.some((prefix) => src.startsWith(prefix))) {
    return src;
  }

  const absoluteSrc = resolveSameOriginPublicAssetUrl(src);
  if (!absoluteSrc) {
    return src;
  }

  const params = new URLSearchParams({
    url: absoluteSrc,
    quality: String(quality),
    format,
  });

  if (width) {
    params.set("width", String(width));
  }

  return `${backendBaseUrl}/api/image-proxy?${params.toString()}`;
}

function rewriteWixImageUrl(src, { width, quality = 72 } = {}) {
  if (!src) {
    return src;
  }

  try {
    const parsed = new URL(src);

    if (parsed.hostname !== "static.wixstatic.com") {
      return src;
    }

    const matched = parsed.pathname.match(/w_(\d+),h_(\d+),al_c,q_(\d+),enc_auto/);

    if (!matched) {
      return src;
    }

    const originalWidth = Number(matched[1]);
    const originalHeight = Number(matched[2]);
    const nextWidth = width ? Math.max(320, Math.min(width, originalWidth)) : originalWidth;
    const nextHeight = Math.max(180, Math.round(originalHeight * (nextWidth / originalWidth)));
    const nextQuality = Math.max(45, Math.min(quality, 82));

    return src.replace(/w_\d+,h_\d+,al_c,q_\d+,enc_auto/, `w_${nextWidth},h_${nextHeight},al_c,q_${nextQuality},enc_auto`);
  } catch {
    return src;
  }
}

export function buildOptimizedAssetUrl(src, { width, quality = 72, format = "webp" } = {}) {
  const resolvedSrc = buildBackendAssetUrl(src);
  const backendBaseUrl = getBackendBaseUrl();

  if (!resolvedSrc || !resolvedSrc.includes("/api/assets/")) {
    const sameOriginOptimized = buildSameOriginProxyImageUrl(resolvedSrc, { width, quality, format });
    if (sameOriginOptimized !== resolvedSrc) {
      return sameOriginOptimized;
    }

    const wixOptimized = rewriteWixImageUrl(resolvedSrc, { width, quality });
    if (resolvedSrc.includes("static.wixstatic.com") && wixOptimized !== resolvedSrc) {
      return wixOptimized;
    }

    return buildExternalProxyImageUrl(wixOptimized, { width, quality, format });
  }

  const [basePath, existingQuery = ""] = resolvedSrc.split("?");
  const params = new URLSearchParams(existingQuery);

  if (width) {
    params.set("width", String(width));
  }
  if (quality) {
    params.set("quality", String(quality));
  }
  if (format) {
    params.set("format", format);
  }

  const queryString = params.toString();
  if (!backendBaseUrl) {
    return queryString ? `${basePath}?${queryString}` : basePath;
  }

  return queryString ? `${backendBaseUrl}${basePath}?${queryString}` : `${backendBaseUrl}${basePath}`;
}

export function buildOptimizedSrcSet(src, widths = [], { quality = 72, format = "webp" } = {}) {
  const uniqueWidths = [...new Set(widths.filter(Boolean).map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0))]
    .sort((left, right) => left - right);

  return uniqueWidths
    .map((width) => {
      const optimizedUrl = buildOptimizedAssetUrl(src, { width, quality, format });
      return optimizedUrl ? `${optimizedUrl} ${width}w` : null;
    })
    .filter(Boolean)
    .join(", ");
}

export function buildOptimizedPictureSources(src, widths = [], { quality = 72, formats = ["avif", "webp"] } = {}) {
  return formats
    .map((format) => {
      const srcSet = buildOptimizedSrcSet(src, widths, { quality, format });
      return srcSet
        ? {
            type: `image/${format}`,
            srcSet,
          }
        : null;
    })
    .filter(Boolean);
}

export function buildResponsiveImageSet(src, { thumbnailWidth = 420, mediumWidth = 820, largeWidth = 1280, quality = 64 } = {}) {
  if (!src) {
    return {
      thumbnailUrl: "",
      mediumUrl: "",
      largeUrl: "",
      originalUrl: "",
    };
  }

  return {
    thumbnailUrl: buildOptimizedAssetUrl(src, { width: thumbnailWidth, quality: Math.max(52, quality - 6), format: "webp" }),
    mediumUrl: buildOptimizedAssetUrl(src, { width: mediumWidth, quality, format: "webp" }),
    largeUrl: buildOptimizedAssetUrl(src, { width: largeWidth, quality: Math.min(quality + 4, 76), format: "webp" }),
    originalUrl: buildBackendAssetUrl(src),
  };
}
